// =============================================================================
// ESPOLIDAR WEB ADMIN - API SERVICE
// =============================================================================

import axios, { AxiosError } from 'axios'
import type {
  User, UserDetail, UserCreate, UserUpdate,
  Device, Zone, ZoneCreate, ZoneUpdate, ZoneCartesianCreate, Media, Trigger, TriggerCreate,
  Event, EventStats, EventWithTriggers, DeviceConfig, LoginCredentials, AuthToken, WorkspaceConfig, WorkspaceConfigCreate,
  Reseller, ResellerDetail, ResellerCreate, ResellerUpdate,
  GlobalStats, TimelineStats, TopZonesStats
} from '../types'

const API_BASE = '/api/v1'

// Axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor per aggiungere token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor per gestire errori auth
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// =============================================================================
// AUTH
// =============================================================================

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthToken> => {
    const formData = new FormData()
    formData.append('username', credentials.username)
    formData.append('password', credentials.password)

    const response = await api.post<AuthToken>('/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  getMe: async (): Promise<User> => {
    const response = await api.get<User>('/auth/me')
    return response.data
  },

  refresh: async (): Promise<AuthToken> => {
    const response = await api.post<AuthToken>('/auth/refresh')
    return response.data
  },
}

// =============================================================================
// DEVICES
// =============================================================================

export const devicesApi = {
  list: async (): Promise<Device[]> => {
    const response = await api.get<Device[]>('/devices/')
    return response.data
  },

  get: async (deviceId: string): Promise<Device> => {
    const response = await api.get<Device>(`/devices/${deviceId}`)
    return response.data
  },

  update: async (deviceId: string, data: Partial<Device>): Promise<Device> => {
    const response = await api.put<Device>(`/devices/${deviceId}`, data)
    return response.data
  },

  delete: async (deviceId: string): Promise<void> => {
    await api.delete(`/devices/${deviceId}`)
  },
}

// =============================================================================
// ZONES
// =============================================================================

export const zonesApi = {
  listByDevice: async (deviceId: string): Promise<Zone[]> => {
    const response = await api.get<Zone[]>(`/zones/device/${deviceId}`)
    return response.data
  },

  get: async (zoneId: number): Promise<Zone> => {
    const response = await api.get<Zone>(`/zones/${zoneId}`)
    return response.data
  },

  create: async (deviceId: string, data: ZoneCreate): Promise<Zone> => {
    const response = await api.post<Zone>(`/zones/device/${deviceId}`, data)
    return response.data
  },

  createCartesian: async (deviceId: string, data: ZoneCartesianCreate): Promise<Zone> => {
    const response = await api.post<Zone>(`/zones/device/${deviceId}/cartesian`, data)
    return response.data
  },

  update: async (zoneId: number, data: ZoneUpdate): Promise<Zone> => {
    const response = await api.put<Zone>(`/zones/${zoneId}`, data)
    return response.data
  },

  delete: async (zoneId: number): Promise<void> => {
    await api.delete(`/zones/${zoneId}`)
  },
}

// =============================================================================
// WORKSPACE
// =============================================================================

export const workspaceApi = {
  get: async (deviceId: string): Promise<WorkspaceConfig> => {
    const response = await api.get<WorkspaceConfig>(`/workspace/device/${deviceId}`)
    return response.data
  },

  update: async (deviceId: string, data: Partial<WorkspaceConfigCreate>): Promise<WorkspaceConfig> => {
    const response = await api.put<WorkspaceConfig>(`/workspace/device/${deviceId}`, data)
    return response.data
  },

  create: async (deviceId: string, data: WorkspaceConfigCreate): Promise<WorkspaceConfig> => {
    const response = await api.post<WorkspaceConfig>(`/workspace/device/${deviceId}`, data)
    return response.data
  },
}

// =============================================================================
// MEDIA
// =============================================================================

export const mediaApi = {
  list: async (mediaType?: string): Promise<Media[]> => {
    const params = mediaType ? { media_type: mediaType } : {}
    const response = await api.get<Media[]>('/media/', { params })
    return response.data
  },

  get: async (mediaId: number): Promise<Media> => {
    const response = await api.get<Media>(`/media/${mediaId}`)
    return response.data
  },

  upload: async (file: File, name?: string): Promise<Media> => {
    const formData = new FormData()
    formData.append('file', file)
    if (name) formData.append('name', name)

    const response = await api.post<Media>('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  delete: async (mediaId: number): Promise<void> => {
    await api.delete(`/media/${mediaId}`)
  },

  download: async (mediaId: number, filename: string): Promise<void> => {
    const response = await api.get(`/media/${mediaId}/download`, {
      responseType: 'blob'
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },

  getPreviewUrl: (filename: string): string => {
    return `/uploads/${filename}`
  },
}

// =============================================================================
// TRIGGERS
// =============================================================================

export const triggersApi = {
  listByDevice: async (deviceId: string): Promise<Trigger[]> => {
    const response = await api.get<Trigger[]>(`/triggers/device/${deviceId}`)
    return response.data
  },

  get: async (triggerId: number): Promise<Trigger> => {
    const response = await api.get<Trigger>(`/triggers/${triggerId}`)
    return response.data
  },

  create: async (deviceId: string, data: TriggerCreate): Promise<Trigger> => {
    const response = await api.post<Trigger>(`/triggers/device/${deviceId}`, data)
    return response.data
  },

  update: async (triggerId: number, data: Partial<TriggerCreate>): Promise<Trigger> => {
    const response = await api.put<Trigger>(`/triggers/${triggerId}`, data)
    return response.data
  },

  delete: async (triggerId: number): Promise<void> => {
    await api.delete(`/triggers/${triggerId}`)
  },
}

// =============================================================================
// CONFIG
// =============================================================================

export const configApi = {
  getDeviceConfig: async (deviceId: string): Promise<DeviceConfig> => {
    const response = await api.get<DeviceConfig>(`/config/device/${deviceId}`)
    return response.data
  },
}

// =============================================================================
// EVENTS
// =============================================================================

export const eventsApi = {
  list: async (params?: {
    event_type?: string
    device_id?: string
    limit?: number
  }): Promise<EventWithTriggers[]> => {
    const response = await api.get<EventWithTriggers[]>('/events/', { params })
    return response.data
  },

  listByDevice: async (
    deviceId: string,
    params?: { event_type?: string; limit?: number }
  ): Promise<Event[]> => {
    const response = await api.get<Event[]>(`/events/device/${deviceId}`, { params })
    return response.data
  },

  getStats: async (deviceId: string, days?: number): Promise<EventStats> => {
    const params = days ? { days } : {}
    const response = await api.get<EventStats>(`/events/stats/${deviceId}`, { params })
    return response.data
  },

  getGlobalStats: async (days?: number): Promise<GlobalStats> => {
    const params = days ? { days } : {}
    const response = await api.get<GlobalStats>('/events/stats/global', { params })
    return response.data
  },

  getTimelineStats: async (params?: {
    device_id?: string
    days?: number
    bucket?: 'day' | 'hour'
  }): Promise<TimelineStats> => {
    const response = await api.get<TimelineStats>('/events/stats/timeline', { params })
    return response.data
  },

  getTopZones: async (params?: {
    device_id?: string
    days?: number
    limit?: number
  }): Promise<TopZonesStats> => {
    const response = await api.get<TopZonesStats>('/events/stats/top-zones', { params })
    return response.data
  },
}

// =============================================================================
// RESELLERS
// =============================================================================

export const resellersApi = {
  list: async (): Promise<ResellerDetail[]> => {
    const response = await api.get<ResellerDetail[]>('/resellers/')
    return response.data
  },

  get: async (id: number): Promise<ResellerDetail> => {
    const response = await api.get<ResellerDetail>(`/resellers/${id}`)
    return response.data
  },

  create: async (data: ResellerCreate): Promise<Reseller> => {
    const response = await api.post<Reseller>('/resellers/', data)
    return response.data
  },

  update: async (id: number, data: ResellerUpdate): Promise<Reseller> => {
    const response = await api.put<Reseller>(`/resellers/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/resellers/${id}`)
  },

  assignDevice: async (resellerId: number, deviceId: string): Promise<void> => {
    await api.post(`/resellers/${resellerId}/assign-device/${deviceId}`)
  },

  unassignDevice: async (resellerId: number, deviceId: string): Promise<void> => {
    await api.delete(`/resellers/${resellerId}/unassign-device/${deviceId}`)
  },

  assignUser: async (resellerId: number, userId: number): Promise<void> => {
    await api.post(`/resellers/${resellerId}/assign-user/${userId}`)
  },

  unassignUser: async (resellerId: number, userId: number): Promise<void> => {
    await api.delete(`/resellers/${resellerId}/unassign-user/${userId}`)
  },

  getDevices: async (resellerId: number): Promise<Device[]> => {
    const response = await api.get<Device[]>(`/resellers/${resellerId}/devices`)
    return response.data
  },

  getUsers: async (resellerId: number): Promise<User[]> => {
    const response = await api.get<User[]>(`/resellers/${resellerId}/users`)
    return response.data
  },
}

// =============================================================================
// IDLE PLAYLIST
// =============================================================================

export interface IdlePlaylistItem {
  id: number
  media_id: number
  position: number
  duration: number
  enabled: boolean
  media?: Media
}

export interface IdlePlaylistItemCreate {
  media_id: number
  position?: number
  duration?: number
  enabled?: boolean
}

export const idlePlaylistApi = {
  get: async (deviceId: string): Promise<IdlePlaylistItem[]> => {
    const response = await api.get<IdlePlaylistItem[]>(`/idle-playlist/device/${deviceId}`)
    return response.data
  },

  update: async (deviceId: string, items: IdlePlaylistItemCreate[]): Promise<IdlePlaylistItem[]> => {
    const response = await api.put<IdlePlaylistItem[]>(`/idle-playlist/device/${deviceId}`, { items })
    return response.data
  },

  addItem: async (deviceId: string, item: IdlePlaylistItemCreate): Promise<IdlePlaylistItem> => {
    const response = await api.post<IdlePlaylistItem>(`/idle-playlist/device/${deviceId}/item`, item)
    return response.data
  },

  deleteItem: async (deviceId: string, itemId: number): Promise<void> => {
    await api.delete(`/idle-playlist/device/${deviceId}/item/${itemId}`)
  },

  clear: async (deviceId: string): Promise<void> => {
    await api.delete(`/idle-playlist/device/${deviceId}`)
  },
}

// =============================================================================
// USERS
// =============================================================================

export const usersApi = {
  list: async (resellerId?: number): Promise<UserDetail[]> => {
    const params = resellerId ? { reseller_id: resellerId } : {}
    const response = await api.get<UserDetail[]>('/users/', { params })
    return response.data
  },

  get: async (id: number): Promise<UserDetail> => {
    const response = await api.get<UserDetail>(`/users/${id}`)
    return response.data
  },

  create: async (data: UserCreate): Promise<User> => {
    const response = await api.post<User>('/users/', data)
    return response.data
  },

  update: async (id: number, data: UserUpdate): Promise<User> => {
    const response = await api.put<User>(`/users/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`)
  },

  toggleActive: async (id: number): Promise<User> => {
    const response = await api.post<User>(`/users/${id}/toggle-active`)
    return response.data
  },
}

// =============================================================================
// ANALYSIS
// =============================================================================

export interface AnalysisEvent {
  id: number
  event_type: string
  event_data: Record<string, unknown> | null
  timestamp: string
  device_id: string
  device_name: string
  zone: {
    id: number
    zone_number: number
    name: string
  } | null
}

export interface AnalysisResponse {
  events: AnalysisEvent[]
  total: number
  skip: number
  limit: number
  filters_applied: {
    device_id: string | null
    zone_id: number | null
    event_type: string | null
    from_date: string | null
    to_date: string | null
  }
}

export interface AnalysisSummary {
  period: {
    from: string
    to: string
  }
  total_events: number
  by_type: Record<string, number>
  by_device: Array<{
    device_id: string
    device_name: string
    count: number
  }>
  by_day: Array<{
    date: string
    count: number
  }>
}

export interface AnalysisZone {
  id: number
  zone_number: number
  name: string
  device_id: string
  device_name: string
}

export const analysisApi = {
  getEvents: async (params?: {
    device_id?: string
    zone_id?: number
    event_type?: string
    from_date?: string
    to_date?: string
    skip?: number
    limit?: number
  }): Promise<AnalysisResponse> => {
    const response = await api.get<AnalysisResponse>('/analysis/events', { params })
    return response.data
  },

  exportEvents: async (params?: {
    device_id?: string
    zone_id?: number
    event_type?: string
    from_date?: string
    to_date?: string
  }): Promise<Blob> => {
    const response = await api.get('/analysis/events/export', {
      params,
      responseType: 'blob'
    })
    return response.data
  },

  getSummary: async (params?: {
    device_id?: string
    from_date?: string
    to_date?: string
  }): Promise<AnalysisSummary> => {
    const response = await api.get<AnalysisSummary>('/analysis/summary', { params })
    return response.data
  },

  getZones: async (device_id?: string): Promise<AnalysisZone[]> => {
    const params = device_id ? { device_id } : {}
    const response = await api.get<AnalysisZone[]>('/analysis/zones', { params })
    return response.data
  },
}

// =============================================================================
// OTA UPDATES
// =============================================================================

export interface FirmwareVersion {
  id: number
  version: string
  filename: string
  file_size: number
  checksum: string
  release_notes: string | null
  is_stable: boolean
  is_active: boolean
  download_count: number
  created_at: string
}

export interface OTAUpdateLog {
  id: number
  device_id: string | null
  device_name: string | null
  previous_version: string | null
  new_version: string
  status: string
  error_message: string | null
  started_at: string | null
  completed_at: string | null
}

export interface TriggerResult {
  device_id: string
  status: string
  message: string
}

export const otaApi = {
  getVersions: async (): Promise<{ versions: FirmwareVersion[], total: number }> => {
    const response = await api.get('/ota/versions')
    return response.data
  },

  getLatestVersion: async (stableOnly: boolean = true): Promise<FirmwareVersion> => {
    const response = await api.get('/ota/versions/latest', { params: { stable_only: stableOnly } })
    return response.data
  },

  uploadVersion: async (formData: FormData): Promise<FirmwareVersion> => {
    const response = await api.post('/ota/versions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  updateVersion: async (versionId: number, data: {
    is_stable?: boolean
    is_active?: boolean
    release_notes?: string
  }): Promise<{ message: string, version: string }> => {
    const response = await api.put(`/ota/versions/${versionId}`, null, { params: data })
    return response.data
  },

  deleteVersion: async (versionId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/ota/versions/${versionId}`)
    return response.data
  },

  triggerUpdate: async (deviceIds: string[], version?: string, force: boolean = false): Promise<{
    message: string
    results: TriggerResult[]
  }> => {
    const response = await api.post('/ota/update/trigger', {
      device_ids: deviceIds,
      version,
      force
    })
    return response.data
  },

  triggerUpdateAll: async (version?: string, force: boolean = false): Promise<{
    message: string
    results: TriggerResult[]
  }> => {
    const response = await api.post('/ota/update/trigger-all', null, {
      params: { version, force }
    })
    return response.data
  },

  getUpdateStatus: async (deviceId?: string): Promise<{ updates: OTAUpdateLog[] }> => {
    const params = deviceId ? { device_id: deviceId } : {}
    const response = await api.get('/ota/updates/status', { params })
    return response.data
  },
}

export default api
